import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

const CANVAS_WIDTH = 320;
const CANVAS_HEIGHT = 240;
const PADDLE_WIDTH = 54;
const PADDLE_HEIGHT = 8;
const PADDLE_Y = CANVAS_HEIGHT - 20;
const PADDLE_SPEED = 5;
const BALL_RADIUS = 4;
const BALL_SPEED = 3;
const BRICK_ROWS = 5;
const BRICK_COLS = 8;
const BRICK_WIDTH = 34;
const BRICK_HEIGHT = 12;
const BRICK_PADDING = 4;
const BRICK_OFFSET_TOP = 28;
const BRICK_OFFSET_LEFT = (CANVAS_WIDTH - (BRICK_COLS * (BRICK_WIDTH + BRICK_PADDING) - BRICK_PADDING)) / 2;
const START_LIVES = 3;
const TICK_MS = 30;

const ROW_COLORS = ['#ffb000', '#33ff66', '#33ff66', '#1fae4a', '#1fae4a'];

interface Brick {
  x: number;
  y: number;
  alive: boolean;
  color: string;
}

type GameStatus = 'playing' | 'won' | 'lost';

@Component({
  selector: 'app-breakout-game',
  host: {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'Breakout mini-game',
    '(document:keydown)': 'onKeydown($event)',
    '(document:keyup)': 'onKeyup($event)'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="breakout-panel">
      <div class="breakout-header">
        <span class="breakout-title">*** BREAKOUT.EXE ***</span>
        <span class="breakout-score">SCORE: {{ score() }}</span>
        <span class="breakout-lives">LIVES: {{ lives() }}</span>
        <button class="breakout-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas #canvasEl [width]="canvasWidth" [height]="canvasHeight" class="breakout-canvas"></canvas>
      @if (status() !== 'playing') {
        <div class="breakout-gameover">
          {{ status() === 'won' ? 'ALL BRICKS CLEARED!' : 'GAME OVER' }} — SCORE {{ score() }} — PRESS ENTER TO RESTART
        </div>
      } @else {
        <div class="breakout-hint">ARROWS / A,D MOVE PADDLE — ESC TO EXIT</div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(0, 0, 0, 0.85);
      overflow: auto;
    }

    .breakout-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: 16px;
      background: #010c05;
      border: 2px solid #33ff66;
      border-radius: 4px;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.45);
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      color: #33ff66;
    }

    .breakout-header {
      display: flex;
      align-items: center;
      gap: 18px;
      width: 100%;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .breakout-title {
      font-weight: bold;
      letter-spacing: 2px;
      color: #ffb000;
      text-shadow: 0 0 6px rgba(255, 176, 0, 0.7);
    }

    .breakout-score,
    .breakout-lives {
      font-size: 0.9rem;
    }

    .breakout-close {
      background: transparent;
      border: 1px solid #33ff66;
      color: #33ff66;
      font-family: inherit;
      padding: 4px 10px;
      cursor: pointer;
      border-radius: 2px;
    }

    .breakout-close:hover {
      background: rgba(51, 255, 102, 0.15);
    }

    .breakout-canvas {
      width: auto;
      height: min(360px, calc(100vh - 180px));
      max-width: calc(100vw - 64px);
      image-rendering: pixelated;
      background: #000;
      border: 1px solid rgba(51, 255, 102, 0.5);
    }

    .breakout-gameover,
    .breakout-hint {
      font-size: 0.85rem;
      text-align: center;
      opacity: 0.9;
    }
  `]
})
export class BreakoutGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');
  private ctx: CanvasRenderingContext2D | null = null;
  private intervalId: ReturnType<typeof setInterval> | undefined;
  private readonly pressedKeys = new Set<string>();

  readonly canvasWidth = CANVAS_WIDTH;
  readonly canvasHeight = CANVAS_HEIGHT;

  readonly close = output<void>();
  readonly score = signal(0);
  readonly lives = signal(START_LIVES);
  readonly status = signal<GameStatus>('playing');

  private paddleX = (CANVAS_WIDTH - PADDLE_WIDTH) / 2;
  private ball = { x: CANVAS_WIDTH / 2, y: PADDLE_Y - BALL_RADIUS - 1, vx: BALL_SPEED * 0.5, vy: -BALL_SPEED };
  private bricks: Brick[] = [];

  ngOnInit(): void {
    this.resetState();
    const canvas = this.canvasRef().nativeElement;
    this.ctx = canvas.getContext('2d');
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
  }

  ngOnDestroy(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }
    if (event.key === 'Enter' && this.status() !== 'playing') {
      this.restart();
      return;
    }
    const key = event.key.toLowerCase();
    if (['arrowleft', 'arrowright', 'a', 'd'].includes(key)) {
      event.preventDefault();
      this.pressedKeys.add(key);
    }
  }

  onKeyup(event: KeyboardEvent): void {
    this.pressedKeys.delete(event.key.toLowerCase());
  }

  requestClose(): void {
    this.close.emit();
  }

  private restart(): void {
    this.resetState();
  }

  private resetState(): void {
    this.paddleX = (CANVAS_WIDTH - PADDLE_WIDTH) / 2;
    this.resetBall();
    this.bricks = [];
    for (let row = 0; row < BRICK_ROWS; row++) {
      for (let col = 0; col < BRICK_COLS; col++) {
        this.bricks.push({
          x: BRICK_OFFSET_LEFT + col * (BRICK_WIDTH + BRICK_PADDING),
          y: BRICK_OFFSET_TOP + row * (BRICK_HEIGHT + BRICK_PADDING),
          alive: true,
          color: ROW_COLORS[row % ROW_COLORS.length]
        });
      }
    }
    this.score.set(0);
    this.lives.set(START_LIVES);
    this.status.set('playing');
    this.pressedKeys.clear();
  }

  private resetBall(): void {
    this.ball = {
      x: this.paddleX + PADDLE_WIDTH / 2,
      y: PADDLE_Y - BALL_RADIUS - 1,
      vx: BALL_SPEED * (Math.random() > 0.5 ? 0.6 : -0.6),
      vy: -BALL_SPEED
    };
  }

  private tick(): void {
    if (this.status() === 'playing') {
      this.updatePaddle();
      this.updateBall();
      this.checkEndConditions();
    }
    this.draw();
  }

  private updatePaddle(): void {
    if (this.pressedKeys.has('arrowleft') || this.pressedKeys.has('a')) {
      this.paddleX = Math.max(0, this.paddleX - PADDLE_SPEED);
    }
    if (this.pressedKeys.has('arrowright') || this.pressedKeys.has('d')) {
      this.paddleX = Math.min(CANVAS_WIDTH - PADDLE_WIDTH, this.paddleX + PADDLE_SPEED);
    }
  }

  private updateBall(): void {
    const ball = this.ball;
    ball.x += ball.vx;
    ball.y += ball.vy;

    if (ball.x - BALL_RADIUS <= 0 || ball.x + BALL_RADIUS >= CANVAS_WIDTH) {
      ball.vx *= -1;
      ball.x = Math.max(BALL_RADIUS, Math.min(CANVAS_WIDTH - BALL_RADIUS, ball.x));
    }
    if (ball.y - BALL_RADIUS <= 0) {
      ball.vy *= -1;
      ball.y = BALL_RADIUS;
    }

    if (
      ball.vy > 0 &&
      ball.y + BALL_RADIUS >= PADDLE_Y &&
      ball.y + BALL_RADIUS <= PADDLE_Y + PADDLE_HEIGHT &&
      ball.x >= this.paddleX &&
      ball.x <= this.paddleX + PADDLE_WIDTH
    ) {
      const hitPos = (ball.x - (this.paddleX + PADDLE_WIDTH / 2)) / (PADDLE_WIDTH / 2);
      ball.vx = BALL_SPEED * hitPos;
      ball.vy = -Math.sqrt(Math.max(BALL_SPEED * BALL_SPEED - ball.vx * ball.vx, 1));
      ball.y = PADDLE_Y - BALL_RADIUS;
    }

    if (ball.y - BALL_RADIUS > CANVAS_HEIGHT) {
      this.lives.update((l) => l - 1);
      if (this.lives() > 0) {
        this.resetBall();
      }
      return;
    }

    for (const brick of this.bricks) {
      if (!brick.alive) {
        continue;
      }
      if (
        ball.x + BALL_RADIUS > brick.x &&
        ball.x - BALL_RADIUS < brick.x + BRICK_WIDTH &&
        ball.y + BALL_RADIUS > brick.y &&
        ball.y - BALL_RADIUS < brick.y + BRICK_HEIGHT
      ) {
        brick.alive = false;
        ball.vy *= -1;
        this.score.update((s) => s + 10);
        break;
      }
    }
  }

  private checkEndConditions(): void {
    if (this.lives() <= 0) {
      this.status.set('lost');
    } else if (this.bricks.every((brick) => !brick.alive)) {
      this.status.set('won');
    }
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#010c05';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    for (const brick of this.bricks) {
      if (!brick.alive) {
        continue;
      }
      ctx.fillStyle = brick.color;
      ctx.fillRect(brick.x, brick.y, BRICK_WIDTH, BRICK_HEIGHT);
    }

    ctx.fillStyle = '#33ff66';
    ctx.fillRect(this.paddleX, PADDLE_Y, PADDLE_WIDTH, PADDLE_HEIGHT);

    ctx.beginPath();
    ctx.fillStyle = '#ffb000';
    ctx.arc(this.ball.x, this.ball.y, BALL_RADIUS, 0, Math.PI * 2);
    ctx.fill();
  }
}
